import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  getDocs,
  doc,
  updateDoc,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from './firebase';
import app from './firebase';
import { type UploadedFile } from './fileUploadService';

export interface NotificationData {
  title: string;
  content: string;
  senderId: string;
  senderName: string;
  senderRole: 'teacher' | 'admin';
  targetClass: string; // e.g., "10-A" or "All Classes"
  priority: 'low' | 'medium' | 'high';
  category: 'announcement' | 'teacher_announcement' | 'fee' | 'exam' | 'assignment' | 'result';
  createdAt: number;
  timestamp: number;
  schoolId: string;
  read: boolean;
}

export interface NotificationInput {
  title: string;
  message: string;
  senderId: string;
  senderName: string;
  senderRole?: 'teacher' | 'admin';
  targetClass?: string;
  priority?: 'low' | 'medium' | 'high';
  category?: 'announcement' | 'teacher_announcement' | 'fee' | 'exam' | 'assignment' | 'result';
  attachments?: UploadedFile[];
}

export interface NotificationStats {
  total: number;
  byCategory: Record<string, number>;
  byClass: Record<string, number>;
  last24Hours: number;
  unreadCount: number;
}

// ===========================
// 1. SEND NOTIFICATION
// ===========================

export async function sendNotification(
  tenantId: string,
  notificationData: NotificationInput
): Promise<{ success: boolean; id?: string; error?: string }> {
  try {
    console.log('📤 Sending notification for tenant:', tenantId);

    const notification: NotificationData & { attachments?: UploadedFile[] } = {
      title: notificationData.title,
      content: notificationData.message,
      senderId: notificationData.senderId,
      senderName: notificationData.senderName,
      senderRole: notificationData.senderRole || 'admin',
      targetClass: notificationData.targetClass || 'All Classes',
      priority: notificationData.priority || 'medium',
      category: notificationData.category || 'announcement',
      createdAt: Date.now(),
      timestamp: Date.now(),
      schoolId: tenantId,
      read: false,
      ...(notificationData.attachments && notificationData.attachments.length > 0
        ? { attachments: notificationData.attachments }
        : {}),
    };

    // Save to Firestore
    const docRef = await addDoc(
      collection(db, `school-data/${tenantId}/notifications`),
      notification
    );

    console.log('✅ Notification saved with ID:', docRef.id);

    // Send FCM push notification to mobile apps
    try {
      await sendFCMPushNotification(tenantId, notificationData);
      console.log('✅ FCM notification sent successfully');
    } catch (fcmError) {
      console.warn('⚠️ FCM notification failed (notification still saved to Firestore):', fcmError);
      // Don't fail the entire operation if FCM fails - notification is still saved
    }

    return { success: true, id: docRef.id };
  } catch (error: any) {
    console.error('❌ Error sending notification:', error);
    return { success: false, error: error.message };
  }
}

// ===========================
// 2. FETCH NOTIFICATIONS (Real-time)
// ===========================

export function listenToNotifications(
  tenantId: string,
  callback: (notifications: any[]) => void,
  limitCount: number = 50
): () => void {
  console.log('🔔 Setting up real-time notification listener for:', tenantId);

  const notificationsRef = collection(db, `school-data/${tenantId}/notifications`);

  const q = query(notificationsRef, orderBy('createdAt', 'desc'), limit(limitCount));

  // Real-time listener
  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      const notifications: any[] = [];

      snapshot.forEach((doc) => {
        notifications.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      console.log('📊 Notifications updated:', notifications.length);
      callback(notifications);
    },
    (error) => {
      console.error('❌ Error listening to notifications:', error);
    }
  );

  return unsubscribe; // Call this to stop listening
}

// ===========================
// 3. FETCH NOTIFICATIONS (One-time)
// ===========================

export async function getNotifications(
  tenantId: string,
  filters: {
    targetClass?: string;
    category?: string;
    limit?: number;
  } = {}
): Promise<any[]> {
  try {
    console.log('🔍 Fetching notifications for tenant:', tenantId);

    const notificationsRef = collection(db, `school-data/${tenantId}/notifications`);

    let q = query(notificationsRef, orderBy('createdAt', 'desc'));

    // Apply filters
    if (filters.targetClass) {
      q = query(q, where('targetClass', '==', filters.targetClass));
    }

    if (filters.category) {
      q = query(q, where('category', '==', filters.category));
    }

    if (filters.limit) {
      q = query(q, limit(filters.limit));
    }

    const snapshot = await getDocs(q);
    const notifications: any[] = [];

    snapshot.forEach((doc) => {
      notifications.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    console.log('📊 Fetched notifications:', notifications.length);
    return notifications;
  } catch (error) {
    console.error('❌ Error fetching notifications:', error);
    return [];
  }
}

// ===========================
// 4. GET NOTIFICATION STATISTICS
// ===========================

export async function getNotificationStats(tenantId: string): Promise<NotificationStats | null> {
  try {
    console.log('📊 Fetching notification stats for tenant:', tenantId);

    const notificationsRef = collection(db, `school-data/${tenantId}/notifications`);
    const snapshot = await getDocs(notificationsRef);

    const stats: NotificationStats = {
      total: snapshot.size,
      byCategory: {},
      byClass: {},
      last24Hours: 0,
      unreadCount: 0,
    };

    const now = Date.now();
    const yesterday = now - 24 * 60 * 60 * 1000;

    snapshot.forEach((doc) => {
      const data = doc.data() as NotificationData;

      // Count by category
      stats.byCategory[data.category] = (stats.byCategory[data.category] || 0) + 1;

      // Count by class
      stats.byClass[data.targetClass] = (stats.byClass[data.targetClass] || 0) + 1;

      // Count last 24 hours
      if (data.createdAt > yesterday) {
        stats.last24Hours++;
      }

      // Count unread
      if (!data.read) {
        stats.unreadCount++;
      }
    });

    console.log('✅ Stats calculated:', stats);
    return stats;
  } catch (error) {
    console.error('❌ Error getting stats:', error);
    return null;
  }
}

// ===========================
// 5. MARK NOTIFICATION AS READ
// ===========================

export async function markNotificationAsRead(
  tenantId: string,
  notificationId: string
): Promise<boolean> {
  try {
    const notificationRef = doc(db, `school-data/${tenantId}/notifications`, notificationId);
    await updateDoc(notificationRef, {
      read: true,
    });
    console.log('✅ Notification marked as read:', notificationId);
    return true;
  } catch (error) {
    console.error('❌ Error marking notification as read:', error);
    return false;
  }
}

// ===========================
// 6. MARK ALL NOTIFICATIONS AS READ
// ===========================

export async function markAllNotificationsAsRead(tenantId: string): Promise<boolean> {
  try {
    console.log('📝 Marking all notifications as read for tenant:', tenantId);

    const notificationsRef = collection(db, `school-data/${tenantId}/notifications`);
    const q = query(notificationsRef, where('read', '==', false));
    const snapshot = await getDocs(q);

    const updatePromises = snapshot.docs.map((doc) =>
      updateDoc(doc.ref, { read: true })
    );

    await Promise.all(updatePromises);

    console.log('✅ Marked', snapshot.size, 'notifications as read');
    return true;
  } catch (error) {
    console.error('❌ Error marking all notifications as read:', error);
    return false;
  }
}

// ===========================
// 7. GET UNREAD NOTIFICATION COUNT
// ===========================

export async function getUnreadNotificationCount(tenantId: string): Promise<number> {
  try {
    const notificationsRef = collection(db, `school-data/${tenantId}/notifications`);
    const q = query(notificationsRef, where('read', '==', false));
    const snapshot = await getDocs(q);

    return snapshot.size;
  } catch (error) {
    console.error('❌ Error getting unread count:', error);
    return 0;
  }
}

// ===========================
// 8. LISTEN TO UNREAD COUNT (Real-time)
// ===========================

export function listenToUnreadCount(
  tenantId: string,
  callback: (count: number) => void
): () => void {
  const notificationsRef = collection(db, `school-data/${tenantId}/notifications`);
  const q = query(notificationsRef, where('read', '==', false));

  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      callback(snapshot.size);
    },
    (error) => {
      console.error('❌ Error listening to unread count:', error);
    }
  );

  return unsubscribe;
}

// ===========================
// 9. SEND FCM PUSH NOTIFICATION (Placeholder)
// ===========================

export async function sendFCMPushNotification(
  tenantId: string,
  notificationData: NotificationInput
): Promise<any> {
  // Determine topic
  let topic: string;
  if (notificationData.targetClass === 'All Classes') {
    topic = `${tenantId}_students`;
  } else {
    // Convert "10-A" to "class_10_a"
    const classCode = (notificationData.targetClass || '')
      .replace('-', '_')
      .toLowerCase();
    topic = `${tenantId}_class_${classCode}`;
  }

  console.log('🔔 Sending FCM to topic:', topic);

  try {
    // Get Firebase Functions instance
    const functions = getFunctions(app);

    // Call the Cloud Function
    const sendNotificationFunction = httpsCallable(functions, 'sendNotification');

    const response = await sendNotificationFunction({
      topic: topic,
      title: notificationData.title,
      message: notificationData.message,
      notificationData: {
        type: notificationData.category || 'announcement',
        priority: notificationData.priority || 'medium',
        targetClass: notificationData.targetClass || 'All Classes',
      },
    });

    console.log('✅ FCM Cloud Function response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Error calling FCM Cloud Function:', error);

    // If Cloud Function is not deployed yet, log warning but don't fail
    if (error.code === 'functions/not-found') {
      console.warn('⚠️ Cloud Function "sendNotification" not found. Deploy functions using: firebase deploy --only functions');
    }

    throw error; // Re-throw so parent can handle gracefully
  }
}
